import { AskConfirmation, buttonsContinueCancel } from '../../../../webmodule-common/other/ui/modal-confirmation';
import { BranchQuoteSupportStatus, QuoteItemConversationType } from '../../../api/dealer-api-interface-franchisee';
import { cache } from '../../cache-impl/cache-registry';
import { compare } from '../../../../webmodule-common/other/clone';
import {
  concatStrings,
  flagInSet,
  isEmptyOrSpace
} from '../../../../webmodule-common/other/ui/string-helper-functions';
import { currentUserClaims, supplierHasFullPrivelages } from '../../../../webmodule-common/other/currentuser-claims';
import { customElement, property, query, state } from 'lit/decorators.js';
import { DataBinding } from '../../../../webmodule-common/other/ui/databinding/databinding';
import {
  DataTracker,
  DynamicValueBinder,
  FieldType
} from '../../../../webmodule-common/other/ui/databinding/data-tracker';
import { DevelopmentError } from '../../../../webmodule-common/other/development-error';
import { emptyGuid, newGuid } from '../../../../webmodule-common/other/api/guid';
import { FormInputAssistant } from '../../../../webmodule-common/other/ui/templateresult/form-input-assistant';
import { FormInputAssistantBase } from '../../../../webmodule-common/other/ui/templateresult/form-input-assistant-base';
import { getApiFactory } from '../../../api/api-injector';
import { getCommentEditModal } from '../../../quotes/ui/quote-item-comment-modal';
import { getQuoteSupplierDisplayName } from '../../../quotes/data/quoteSupplierProvider';
import { getSupportStatusBadge } from '../../conversation/conversation-helper';
import { html, nothing } from 'lit';
import { information } from '../../../../webmodule-common/other/ui/modal-option';
import { InformationDispatcher } from '../../../../webmodule-common/other/ui/information-dispatcher';
import { LitElementBase } from '../../../../webmodule-common/other/litelement-base';
import { money } from '../../../../webmodule-common/other/currency-formatter';
import { SaveWorkflowModal } from '../../../../webmodule-common/other/save-workflow';
import { stringsAsMarkdownList } from '../../../../webmodule-common/other/general/markdown';
import { updateFreehandPrice } from '../data/v6/freehand-pricing';
import type {
  AddConversationAttachment,
  BranchQuoteSupport,
  QuoteItemConversation,
  StockLookupView,
  ViewConversationAttachment,
  ViewConversationEntry
} from '../../../api/dealer-api-interface-franchisee';
import type { BranchQuoteConversationView, ConversationActions } from '../../conversation/wm-branchquote-conversation';
import type {
  ConversationEntryUpdateEvent,
  CreateConversationEntry,
  WebModuleConversationEntryEditorView
} from '../../conversation/wm-conversation-entry';
import type {
  EventValueGetter,
  EventValueSetter
} from '../../../../webmodule-common/other/ui/databinding/data-tracker';
import type { FormInputImageFileUploadView } from '../../../../webmodule-common/other/ui/templateresult/FormInputView';
import type { FranchiseeQuoteContainerManager } from '../data/franchisee-quote-manager';
import type { InputUpdateQuoteItems } from '../../../api/dealer-api-interface-quote';
import type { PropertyValueMap, TemplateResult } from 'lit';
import type { QuoteItemContainer } from '../../../quotes/data/quote-item-container';
import { LangFranchiseeEngineeredToOrder } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-engineered-to-order-lang';
import {
  compareFrames,
  FrameCompareReport,
  FrameValidationType,
  getFrameCompareAsMarkdown,
  v6FrameUserMode,
  V6QuoteFrameEditor,
  V6QuoteFrameEditorData
} from '../../../../webmodule-common/interop/webmodule-interop';
import { offline } from '../../../../webmodule-common/other/ui/offline';
import { toJsonStr } from '../../../../webmodule-common/other/blob-converters';
import {
  applyV6PricesToQuoteItemPriceWithSupplierAdjustmentCorrections,
  getV6FrameForContainer,
  getV6QuoteItemForContainer
} from '../../../quotes/data/v6/helper-functions';
import { sleep } from '../../../../webmodule-common/other/general/time';
import { v6Util } from '../../../v6config/v6config';
import { LangFranchiseeQuoteManager } from '../../../../localization/scripts/dealer-franchisee/quotes/data/franchisee-quote-manager-lang';
import { fireQuickInformationToast } from '../../../../webmodule-common/other/toast-away';

export interface ETOEditorSavedEventData {
  quoteItemContainer: QuoteItemContainer;
  branchQuoteSupport: BranchQuoteSupport;
  masterDocument: ViewConversationEntry;
  quoteItemConversationLink: QuoteItemConversation;
}

@customElement('wmview-franchisee-eto-editor')
export class WebModuleFranchiseeETOEditor extends LitElementBase {
  async requestSave() {
    const isSavedPromise = this.dispatchCustom<{ event: Promise<boolean> | null }>('needsave', { event: null }).event;
    return isSavedPromise ? await isSavedPromise : true;
  }
  isEditingDraft: boolean = false;
  @property({ type: Object }) v6Editor: V6QuoteFrameEditor | null = null;
  @property({ type: Object }) branchQuoteSupport?: BranchQuoteSupport;
  @property({ type: Object }) quoteManager?: FranchiseeQuoteContainerManager;
  @property({ type: Boolean }) isNewETO = true;
  @property({ type: Object }) quoteItemConversationLink?: QuoteItemConversation | null;
  @query('#conversationETO') conversation?: BranchQuoteConversationView;
  @query('#masterdocument') masterDocumentEditor?: WebModuleConversationEntryEditorView;
  protected _originalsupplierGrossSingleUnitCost?: number;
  protected _originalQty?: number;
  protected _originalThumbnail?: string;
  protected dataBinding = new DataBinding(this);
  protected dataTracker = new DataTracker(this.dataBinding);
  protected franchiseeApi = getApiFactory().franchisee();
  protected claims = currentUserClaims();
  protected conversationEntryId: string = newGuid();
  protected quoteApi = getApiFactory().quote();
  @query('#thumbnail') protected thumbNailEditor?: FormInputImageFileUploadView;
  @state() protected addNoteTemplate?: TemplateResult;
  @state() protected editNoteTemplate?: TemplateResult;

  @property({ type: Boolean }) public isFrameBasedRequest?: boolean = false;

  get usingV6() {
    return this.v6Editor !== null;
  }
  constructor() {
    super();
    this.prepareBindings();
  }

  get conversationActions(): ConversationActions | undefined {
    return this.conversation?.actionsTemplate();
  }
  protected _masterDocumentConversationId: string = newGuid();

  //this is both conversation and entryid
  get masterDocumentConversationId() {
    return this.masterDocument?.conversationId ?? this._masterDocumentConversationId;
  }

  @property({ type: Object }) private _quoteItemContainer?: QuoteItemContainer | undefined;

  public get quoteItemContainer(): QuoteItemContainer | undefined {
    return this._quoteItemContainer;
  }
  public set quoteItemContainer(value: QuoteItemContainer | undefined) {
    const resetValues = !value || !this._quoteItemContainer || this._quoteItemContainer?.item.id !== value?.item.id;
    this._quoteItemContainer = value;
    if (resetValues) {
      this.backupPriceDetails();
    }
  }

  @property({ type: Object }) private _masterDocument?: ViewConversationEntry | undefined;

  public get masterDocument(): ViewConversationEntry | undefined {
    return this._masterDocument;
  }

  public set masterDocument(value: ViewConversationEntry | undefined) {
    this._masterDocument = value;
  }

  public get readonly() {
    return super.readonly || this.forceReadonly;
  }

  get isEditorVisible() {
    return this.addNoteTemplate !== undefined || this.editNoteTemplate !== undefined;
  }

  get qcm(): FranchiseeQuoteContainerManager {
    if (!this.quoteManager) throw new DevelopmentError('missing quote manager');
    return this.quoteManager;
  }

  get forceReadonly() {
    return (this.quoteManager?.isReadonly() ?? true) || this.ticketClosed;
  }

  get quoteId(): string {
    return this.qcm.quoteId;
  }

  get quoteSetId() {
    return this.qcm.quote.quoteSetId;
  }

  get branchQuoteSupportId(): string {
    return this.branchQuoteSupport?.id ?? this.quoteItemContainer?.item.id ?? emptyGuid;
  }

  get quoteItemId() {
    return this.quoteItemContainer?.item.id ?? emptyGuid;
  }

  get hasDataChanged(): boolean {
    if (!this.quoteItemContainer) return false;

    // When it is a new item we return true as there is no backup item, and the supplier thumbnail nor master document
    // has not been uploaded yet.
    if (this.isNewETO) return true;

    return (
      this.priceOrQtyChanged ||
      (this.v6Editor && this.v6Editor.changed) ||
      this.masterDocument?.lastModified === '' ||
      this.thumbNailEditor?.isValueChanged ||
      this.qcm.changedItem(this.quoteItemContainer.item.id)
    );
  }

  protected get priceOrQtyChanged() {
    if (!this.quoteItemContainer) return false;
    if (this.isNewETO) return true;
    return (
      this._originalsupplierGrossSingleUnitCost !== this.quoteItemContainer.price.supplierGrossSingleUnitCost ||
      this._originalQty !== this.quoteItemContainer.item.quantity
    );
  }

  protected get ticketClosed() {
    return (
      !this.branchQuoteSupport ||
      // eslint-disable-next-line no-bitwise
      flagInSet(this.branchQuoteSupport.status, BranchQuoteSupportStatus.Cancelled | BranchQuoteSupportStatus.Resolved)
    );
  }

  async getResolveIssues(): Promise<string[]> {
    const result = this.getValidationErrors();

    if (!this.quoteItemContainer) throw new DevelopmentError('ETO must have item');

    if (!this.usingV6 && isEmptyOrSpace(this.quoteItemContainer.item.description))
      result.push(LangFranchiseeEngineeredToOrder.specialItemReferenceMustBeSet());

    if (this.usingV6) {
      var data = getV6FrameForContainer(this.quoteItemContainer);
      const stuff = data?.validations?.filter(x => x.type === FrameValidationType.critical) ?? [];
      stuff.forEach(issue => {
        result.push(`${issue.title}: ${issue.text ?? ''}`);
      });
    }
    if (this.quoteItemContainer.item.quantity < 1) result.push(LangFranchiseeEngineeredToOrder.quantityMustBeValid());
    if (this.quoteItemContainer.item.quantity % 1 !== 0)
      result.push(LangFranchiseeEngineeredToOrder.quantityMustBeWholeNumber());

    if (isEmptyOrSpace(this.thumbNailEditor?.value))
      result.push(LangFranchiseeEngineeredToOrder.thumbnailMustBeUploaded());

    if ((this.masterDocument?.attachments.length ?? 0) < 1)
      result.push(LangFranchiseeEngineeredToOrder.shopDrawingFileMustBeIncluded());

    return result;
  }

  getValidationErrors(): string[] {
    const errors: string[] = [];
    this.dataTracker.applyChangeToValue();
    if (!this.quoteItemContainer) throw new DevelopmentError('ETO QuoteItemContainer must exist');

    if (isEmptyOrSpace(this.quoteItemContainer.item.title))
      errors.push(LangFranchiseeEngineeredToOrder.pleaseAddTitleToDescribeSpecialItem());

    const quantity = this.quoteItemContainer?.item.quantity ?? 0;
    if (quantity <= 0) {
      errors.push(LangFranchiseeEngineeredToOrder.pleaseProvideQuantityGreaterThanZero());
    }
    if (quantity % 1 !== 0) {
      errors.push(LangFranchiseeEngineeredToOrder.specialItemQuantityMustBeWholeNumber());
    }

    return errors;
  }

  async prepareEditor(): Promise<void> {}
  async prepareForSave() {
    await this.v6BusyCheck();
    this.dataTracker.applyChangeToValue();
  }
  private buyInDataJsonStr(buyInCosts?: StockLookupView[]): string | undefined {
    if (buyInCosts && buyInCosts.length > 0) {
      return toJsonStr(buyInCosts) ?? undefined;
    } else return undefined;
  }
  async save(): Promise<boolean> {
    if (!this.quoteItemContainer) return false;

    if (!this.hasDataChanged) return true;

    const qm = this.qcm;
    const informationDispatcher = new InformationDispatcher();

    const setInfo = async (s: string, header = LangFranchiseeEngineeredToOrder.preparingToSave()) => {
      await informationDispatcher.setInformation(`# ${header}

                + ${s}`);
    };

    await setInfo(LangFranchiseeEngineeredToOrder.gettingUpdatesFromSupplier());

    const saveModal = new SaveWorkflowModal(
      LangFranchiseeEngineeredToOrder.savingItemTitle({ title: this.quoteItemContainer.item.title }),
      informationDispatcher,
      3
    );

    await saveModal.delayedShow();
    //await modalState.onShow;

    try {
      await setInfo(LangFranchiseeEngineeredToOrder.requestingUpdatesFromSupplier());
      //process ssi with new information
      //build an ssi processor that will build the quote exluding the deleted item, so that we get data
      //based on its non-existence
      let requiresSSI = false;
      const ssiProcessor = qm.ssiProcessor();

      if (!this.usingV6) {
        //we need to go and get the correct price and store it in the item
        //before we run ssi data collection.
        if (this.priceOrQtyChanged) {
          if (
            !(await updateFreehandPrice(
              this.qcm.quote.supplierId,
              this.qcm.quote.quoteOwnerId,
              this.quoteItemContainer.item,
              this.quoteItemContainer.price
            ))
          ) {
            await information(LangFranchiseeEngineeredToOrder.saveCancelledCouldNotCommunicateWithServer());
            return false;
          }
          requiresSSI = true;
        }
      } else {
        //V6 CHANGES

        if (!this.quoteManager) throw new DevelopmentError('FranchiseeQuoteContainerManager must be assigned');

        if (this.usingV6 && this.v6Editor && this.quoteItemContainer) {
          if (!this.quoteItemContainer.data)
            throw new DevelopmentError('EngineeredToOrderLineItemView, quoteItemContainer.data is null');

          let details: V6QuoteFrameEditorData | null = null;
          try {
            await this.v6BusyCheck();

            const attr = this.v6Editor.modifiedAttributes;
            if (
              this.isNewETO ||
              attr.length > 0 ||
              this.quoteManager.changedItemQuantity(this.quoteItemContainer?.item.id)
            ) {
              details = await this.v6Editor.commit();
            }
          } catch {
            await offline();
          }
          if (details) {
            requiresSSI = true;
            const buyIn = details?.buyInItems;

            const buyInCosts = await this.quoteManager.getBuyInCosts(buyIn);
            const buyInMetaData = this.buyInDataJsonStr(buyInCosts);
            const quoteItem = getV6QuoteItemForContainer(this.quoteItemContainer);

            const originalFrameData = quoteItem ? v6Util().getFrameData(quoteItem as any) : null;
            const newFrameData = details.quoteItem ? v6Util().getFrameData(details.quoteItem as any) : null;
            const frameDiff = compareFrames(originalFrameData, newFrameData);
            this.quoteItemContainer.differenceReport = frameDiff;

            this.quoteItemContainer.item.isRestrictedToPowerUser =
              details.requiredUserMode == v6FrameUserMode.powerUser;

            applyV6PricesToQuoteItemPriceWithSupplierAdjustmentCorrections(
              this.quoteItemContainer.item.quantity,
              details.price,
              this.quoteItemContainer.price,
              buyInCosts
            );

            this.quoteItemContainer.data.providerData = toJsonStr(details.quoteItem) ?? null;

            this.quoteItemContainer.buyInData = buyInMetaData ?? undefined;

            // if the pricing from V6 is different from what we originally had, clear any supplier price adjustment.
          }
        }

        //END V6 CHANGES
      }
      const ssiInputs = requiresSSI ? await ssiProcessor.processSSI() : undefined;

      await setInfo(LangFranchiseeEngineeredToOrder.supplierProcessingCompleteSendingUpdatesToServer());

      if (this.isNewETO) {
        if (!(await this.createNewItem(ssiInputs))) return false;
      } else {
        if (!(await this.saveExistingItem(ssiInputs))) return false;
      }
      this.v6Editor?.resetEditorState();
      this.backupPriceDetails();
    } finally {
      saveModal?.done();
      await saveModal?.hideModal();
    }

    return true;
  }
  async v6BusyCheck() {
    if (this.usingV6 && this.v6Editor) {
      await sleep(20);
      while (this.v6Editor?.busy) {
        await sleep(20);
      }
    }
  }

  async createNewItem(ssiInputs: InputUpdateQuoteItems | undefined): Promise<boolean> {
    if (!this.quoteItemContainer) return false;
    const thumbnail = this.thumbNailEditor?.isValueChanged ? await this.thumbNailEditor?.base64Value() : '';

    const thumbnailExtension = thumbnail !== '' ? this.thumbNailEditor?.fileExtension : undefined;
    const result = await this.qcm.createQuoteItemEx(this.quoteItemContainer, thumbnail, ssiInputs, thumbnailExtension);
    if (!this.qcm.lastSaveSuccessful) return false;

    if (result) {
      await this.thumbNailEditor?.ClearFiles();
      this.isNewETO = false;
      this.quoteItemContainer = result;
      await this.assignBranchQuoteSupport();
      this.dispatchSavedEvent();
      return true;
    }
    return false;
  }

  async assignBranchQuoteSupport() {
    if (!this.quoteManager) throw new DevelopmentError('ETO QuoteManager must be assigned');
    if (!this.quoteItemContainer) throw new DevelopmentError('ETO QuoteItemcontainer must be assigned');
    if (!this.branchQuoteSupport) throw new DevelopmentError('ETO branchQuoteSupport must be assigned');
    if (!isEmptyOrSpace(this.branchQuoteSupport.recordVersion))
      throw new DevelopmentError('ETO branchQuoteSupport already exists');
    if (!this.masterDocument) throw new DevelopmentError('ETO MasterDocument must be assigned');
    const api = this.franchiseeApi;
    const attachments = this.masterDocumentEditor?.allAttachmentsAsInputs ?? [];
    const branchSupportSubject = concatStrings(
      { excludeEmpty: true, split: ' - ' },
      this.quoteItemContainer.item.title,
      this.quoteItemContainer.item.description
    );
    const result = await api.addBranchQuoteSupport({
      quoteItemLink: null,
      masterDocumentConversationEntry: {
        attachments: attachments,
        id: this.masterDocument.id,
        text: this.masterDocument.text,
        users: []
      },
      branchQuoteId: this.qcm.quoteId,
      conversationId: this.branchQuoteSupport.conversationId,
      conversationEntry: null, //we may have already created the first one.
      id: this.branchQuoteSupport.id,
      status: this.branchQuoteSupport.status,
      subject: branchSupportSubject,
      type: this.branchQuoteSupport.type
    });
    if (!result) return false;
    this.branchQuoteSupport = result.branchQuoteSupport;
    this.masterDocument = result.masterDocument ?? undefined;

    //if we are creating the support ticket, then this is the very first referrer to the ticket
    //so we can create it sharing the key.
    this.quoteItemConversationLink = await api.quoteItemConversationLink({
      quoteId: this.quoteManager?.quoteId,
      conversationId: this.quoteItemContainer.item.id,
      quoteItemId: this.quoteItemContainer.item.id,
      type: QuoteItemConversationType.EngineeredToOrder
    });
    return true;
  }

  async revertChanges(): Promise<boolean> {
    if (!this.quoteItemContainer) return true;
    this.quoteItemContainer =
      this.qcm.restoreQuoteItemFromBackup(this.quoteItemContainer.item.id) ?? this.quoteItemContainer;

    await this.thumbNailEditor?.ClearFiles();

    this.dataTracker.resetEditorValue();
    return true;
  }

  prepareBindings() {
    const addDynamicMoney = (
      fieldName: string,
      getter: EventValueGetter,
      setter: EventValueSetter,
      readonly?: () => boolean
    ) => {
      this.dataTracker.addBinding(
        new DynamicValueBinder(FieldType.money, false, getter, setter, readonly),
        fieldName,
        fieldName,
        FieldType.money,
        false
      );
    };

    this.dataTracker.addObjectBinding(() => this.quoteItemContainer?.item, 'title', 'title', FieldType.string, false);

    this.dataTracker.addDynamic(
      'comment',
      FieldType.string,
      () => {
        return this.quoteItemContainer?.item?.comment;
      },
      () => {},
      { nullable: true }
    );
    this.dataTracker.addDynamic(
      'commentToManufacturer',
      FieldType.string,
      () => {
        return this.quoteItemContainer?.item?.commentToManufacturer;
      },
      () => {},
      { nullable: true }
    );

    this.dataTracker.addObjectBinding(
      () => this.quoteItemContainer?.item,
      'description',
      'description',
      FieldType.string,
      false
    );
    this.dataTracker.addObjectBinding(
      () => this.quoteItemContainer?.item,
      'quantity',
      'quantity',
      FieldType.float,
      false
    );

    addDynamicMoney(
      'supplierGrossSingleUnitCost',
      () => this.quoteItemContainer?.price.supplierGrossSingleUnitCost ?? 0,
      () => {
        console.log('singleUnitCost value set');
      },
      () => true
    );
  }

  connectedCallback(): void {
    super.connectedCallback();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
  }

  public branchSupportActionsTemplate(): unknown {
    const a = this.conversationActions;
    if (!a) return html``;
    return html`${a.postToSupplier(true)}${a.reactivate(true)}${a.resolve(true)}`;
  }

  conversationNoteHeader(): unknown {
    let title = '';
    if (this.isEditorVisible) {
      const editCommentHeader = this.isEditingDraft
        ? LangFranchiseeEngineeredToOrder.editDraftComments()
        : LangFranchiseeEngineeredToOrder.editComments();
      title =
        this.isNewETO || (this.conversation?.results?.conversationEntries.length ?? 0) == 0
          ? LangFranchiseeEngineeredToOrder.supportInitialTopic()
          : this.addNoteTemplate !== undefined
            ? LangFranchiseeEngineeredToOrder.addComments()
            : editCommentHeader;
    }
    return html`<h2 class="main-header-style">${title}</h2>`;
  }

  conversationNoteTemplates(): unknown {
    if (this.addNoteTemplate !== undefined) return this.addNoteTemplate;
    if (this.editNoteTemplate !== undefined) return this.editNoteTemplate;
    return html``;
  }

  protected firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    this.requestUpdate(); // we need to build once, so we can get the conversation actions;
  }

  /**
   * This is only used when not using V6 as the editor
   * @returns the cost of the item based on the quantity and the supplierGrossSingleUnitCost.
   */
  protected quantityCost(): number {
    try {
      const qty = (this.dataTracker.getEditorValue('quantity') as number) ?? 0;
      const unitCost = this.quoteItemContainer?.price.supplierGrossSingleUnitCost ?? 0;
      return money(qty * unitCost, 2);
    } catch {
      return this.quoteItemContainer?.price.quantityCost ?? 0;
    }
  }
  get quantityFromUi(): number {
    return this.dataTracker.getEditorValue('quantity') as number;
  }

  /**
   * only used when not using V6 as the editor
   * @param updateCost
   * @returns
   */

  protected eventCostOrQuantityChanged(updateCost: boolean) {
    if (!this.quoteItemContainer || this.usingV6) return;

    const qtyCost = this.quantityCost();
    const unitCost = this.dataTracker.getEditorValue('supplierGrossSingleUnitCost') as number;
    const qty = this.dataTracker.getEditorValue('quantity') as number;

    if (updateCost) {
      //Set the unit cost. This is also an item being sent to the supplier, so we need to set the supplier unit cost.
      //the single unit cost needs to be force refreshed from v6
      this.quoteItemContainer.price.singleUnitCost = 0;
      this.quoteItemContainer.price.supplierGrossSingleUnitCost = unitCost;
    }

    if (money(qty * unitCost, 2) !== qtyCost) {
      this.requestUpdate();
    }
  }
  protected eventChangedSingleUnitCost = () => this.eventCostOrQuantityChanged(true);
  protected eventChangedQuantity = () => this.eventCostOrQuantityChanged(false);

  protected eventNewConversationEntry = (e: CustomEvent<{ enabled: boolean; editor: TemplateResult }>) => {
    this.isEditingDraft = false;
    if (e.detail.enabled) {
      this.addNoteTemplate = e.detail.editor;
      this.editNoteTemplate = undefined;
    } else this.addNoteTemplate = undefined;
  };

  protected eventConversationSaved = async (
    e: CustomEvent<{
      updateEvent: ConversationEntryUpdateEvent;
      showMessage: boolean;
      completionPromise: Promise<void>;
    }>
  ) => {
    if (this.isNewETO) {
      e.detail.showMessage = false;
      this.prepareForSave();
      e.detail.completionPromise = (async () => {
        await this.save();
      })();
    }
  };

  protected eventConversationCreated = async (
    e: CustomEvent<{
      updateEvent: CreateConversationEntry;
      showMessage: boolean;
      completionPromise: Promise<void>;
    }>
  ) => {
    if (this.isNewETO) {
      e.detail.showMessage = false;
      this.prepareForSave();
      e.detail.completionPromise = (async () => {
        await this.save();
      })();
    }
  };

  protected eventConversationChanged = async (
    e: CustomEvent<{
      branchQuoteSupport: BranchQuoteSupport;
    }>
  ) => {
    this.branchQuoteSupport = e.detail.branchQuoteSupport;
    this.prepareForSave();
    if (this.hasDataChanged) await this.save();
    else this.dispatchSavedEvent();
  };

  protected eventConversationChanging = async (
    e: CustomEvent<{
      branchQuoteSupport: BranchQuoteSupport;
      status: BranchQuoteSupportStatus;
      allowChange: Promise<boolean>;
      validations: string[];
    }>
  ) => {
    let issues: string[] = [];
    e.detail.allowChange = (async () => {
      let allowChange = false;
      let errors: string[] = [];

      switch (e.detail.status) {
        case BranchQuoteSupportStatus.Cancelled:
          allowChange = await AskConfirmation(
            LangFranchiseeEngineeredToOrder.cancelEtoNotification(),
            buttonsContinueCancel(),
            undefined,
            LangFranchiseeEngineeredToOrder.continueSpecialItemBqsCancel()
          );
          break;
        case BranchQuoteSupportStatus.Resolved:
          //we need to make sure the thing is saved before we can resolve it.

          const isSaved = await this.requestSave();
          if (!isSaved) {
            allowChange = false;
            break;
          }
          issues = await this.getResolveIssues();
          if (issues.length > 0) {
            await information(
              LangFranchiseeEngineeredToOrder.resolveEtoNotification({ issues: stringsAsMarkdownList(issues) }),
              LangFranchiseeEngineeredToOrder.specialItemValidationIssues()
            );
            allowChange = false;
          } else allowChange = true;
          break;
        case BranchQuoteSupportStatus.New:
          errors = this.getValidationErrors();
          if (this.branchQuoteSupport) {
            if ((this.conversation?.results?.conversationEntries.length ?? 0) === 0) {
              const quoteSummary = (await cache().quote.getData(this.branchQuoteSupport.branchQuoteId))?.quoteSummary;
              const supplierName = await getQuoteSupplierDisplayName(quoteSummary?.supplierId);
              // message specified via PBI 218417
              errors.push(LangFranchiseeEngineeredToOrder.etoMissingSupportInitialTopicMsg(supplierName));
            }
          }
          if (errors.length != 0) {
            e.detail.validations.push(...errors);
            allowChange = false;
          }
          allowChange = true;
          break;
        default:
          allowChange = true;
      }

      if (allowChange) {
        await this.requestSave();
        //this.prepareForSave();
        //await this.save();
      }

      return allowChange;
    })();
  };

  protected eventEditConversationEntry = (
    e: CustomEvent<{
      enabled: boolean;
      editor: TemplateResult;
      entry: ViewConversationEntry;
    }>
  ) => {
    this.isEditingDraft = false;
    if (e.detail.enabled) {
      this.isEditingDraft = e.detail.entry.draft;
      this.editNoteTemplate = e.detail.editor;
      this.addNoteTemplate = undefined;
    } else this.editNoteTemplate = undefined;
  };

  private createCommentButton(fieldName: string, forms: FormInputAssistant): { label: string; readonly: boolean } {
    const currentValue = this.dataTracker.getObjectValue(fieldName) as string;

    if (forms.forceReadonly) {
      return {
        label: LangFranchiseeEngineeredToOrder.viewNote(),
        readonly: !currentValue || isEmptyOrSpace(currentValue)
      };
    }

    return {
      label: isEmptyOrSpace(currentValue)
        ? LangFranchiseeEngineeredToOrder.addNote()
        : LangFranchiseeEngineeredToOrder.editNote(),
      readonly: false
    };
  }

  private async handleCommentEdit(fieldName: string, title: string, forms: FormInputAssistant): Promise<void> {
    if (!this.quoteItemContainer) return;
    if (fieldName !== 'commentToManufacturer' && fieldName !== 'comment') return;

    const currentValue = this.dataTracker.getObjectValue(fieldName) as string;
    const modal = await getCommentEditModal(undefined, title, forms.forceReadonly, currentValue);

    if (modal.result === 'update') {
      if (fieldName === 'comment') this.quoteItemContainer.item.comment = modal.comment ?? null;
      else if (fieldName === 'commentToManufacturer')
        this.quoteItemContainer.item.commentToManufacturer = modal.comment ?? null;

      this.requestUpdate();
    }
  }

  private createCommentRow(
    forms: FormInputAssistantBase,
    title: string,
    tooltip: string,
    buttonConfig: { label: string; readonly: boolean },
    onClick: () => void
  ): TemplateResult {
    return forms.multiEditRow({
      title,
      toolTip: tooltip,
      slotted: forms.buttonPrimary(buttonConfig.label, {
        events: { click: onClick },
        readonly: buttonConfig.readonly
      })
    });
  }

  protected render(): unknown {
    //the readonly state of the object is not 100% reliable. we need to check many states.
    const readonly = this.forceReadonly;
    const forms = new FormInputAssistant(this.dataTracker, readonly, true);
    const formsNonForced = new FormInputAssistantBase(false);

    const customerNoteButton = this.createCommentButton('comment', forms);
    const manufacturerNoteButton = this.createCommentButton('commentToManufacturer', forms);

    const handleCustomerNote = () =>
      this.handleCommentEdit('comment', LangFranchiseeEngineeredToOrder.notesToClient(), forms);

    const handleManufacturerNote = () =>
      this.handleCommentEdit('commentToManufacturer', LangFranchiseeEngineeredToOrder.notesToManufacturer(), forms);

    const alwaysShowEditor = !readonly && this.branchQuoteSupport && !this.ticketClosed;

    const conversation = this.branchQuoteSupport
      ? html`
          <div class="border-top border-secondary mt-2 ">
            <wm-branchquoteconversationview
              .alwaysShowNewNoteEditor="${alwaysShowEditor ?? true}"
              .readonly="${this.quoteManager?.isReadonly() ?? true}"
              .showHeader=${false}
              .showSubject=${false}
              .showQuoteItemLink=${false}
              .useExternalEditing="${true}"
              id="conversationETO"
              .branchQuoteSupport=${this.branchQuoteSupport}
              .emptyNote=""
              .supplierName=${this.quoteManager?.supplierName}
              ?sortAscending=${true}
              @wm-event-new-entry="${this.eventNewConversationEntry}"
              @wm-event-edit-entry="${this.eventEditConversationEntry}"
              @wm-event-conversation-saved="${this.eventConversationSaved}"
              @wm-event-conversation-created="${this.eventConversationCreated}"
              @wm-event-changed="${this.eventConversationChanged}"
              @wm-event-changing="${this.eventConversationChanging}"
            ></wm-branchquoteconversationview>
          </div>
        `
      : html``;

    const src = isEmptyOrSpace(this.quoteItemContainer?.item.virtualThumbnailPath)
      ? ''
      : this.quoteApi.api.fullUrl(`api/file/${this.quoteItemContainer?.item.virtualThumbnailPath}`);

    const isSupplier = this.claims.isAgent;
    const dealerAccess = !isSupplier || (isSupplier && supplierHasFullPrivelages());

    const eventAttachmentClicked = (e: CustomEvent<{ filename: string }>) => {
      const attachment = this.masterDocument?.attachments.find(x => x.filename === e.detail.filename);
      if (attachment && !isEmptyOrSpace(attachment.publicUrl)) window.open(attachment.publicUrl, '_blank');
    };

    const eventMasterDocument = async (
      e: CustomEvent<{
        attachments: AddConversationAttachment[];
        conversationAttachments: ViewConversationAttachment[];
      }>
    ) => {
      if (this.masterDocument) {
        if (!compare(this.masterDocument.attachments, e.detail.conversationAttachments)) {
          this.masterDocument.lastModified = '';
          this.masterDocument.attachments = e.detail.conversationAttachments;
        }
      }
    };
    const convversationTemplate = html`<div class="conversation-header-block">
        <h2 class="main-header-style border-0">${LangFranchiseeEngineeredToOrder.comments()}</h2>
        ${this.conversationActions?.attachmentsOnlySwitch()}
      </div>
      <div class="eto-edit-conversation">${conversation}</div>`;
    const tabGroup = this.usingV6
      ? html`<webmodule-tab-group size="small">
          <webmodule-tab slot="nav" panel="optionsTab" active>
            ${LangFranchiseeEngineeredToOrder.optionsTab()}
          </webmodule-tab>
          <webmodule-tab slot="nav" panel="conversationTab">
            ${LangFranchiseeEngineeredToOrder.conversationTab()}
          </webmodule-tab>
          <webmodule-tab-panel name="optionsTab"> ${this.v6Editor?.ui} </webmodule-tab-panel>
          <webmodule-tab-panel name="conversationTab"> ${convversationTemplate} </webmodule-tab-panel>
        </webmodule-tab-group> `
      : html``;

    const masterDocumentDropZoneTemplate = html` <div class="eto-master-document">
      <wmview-conversation-entry-editor
        id="masterdocument"
        @wm-event-attachmentclicked="${eventAttachmentClicked}"
        .allowDownloadLinks="${true}"
        .attachmentsCaption=${LangFranchiseeEngineeredToOrder.shopDrawing()}
        .maxFiles="${1}"
        .readonly="${readonly || !isSupplier}"
        .forceUpdate="${true}"
        .attachmentsOnly="${true}"
        .autoSaveWhenAttachmentsOnly="${false}"
        .conversationEntry="${this.masterDocument}"
        .showHeader="${false}"
        @wm-event-attachment-uploaded="${eventMasterDocument}"
      ></wmview-conversation-entry-editor>
    </div>`;

    // Left column template
    const leftColumnTemplate = html`
      <div class="main-header d-flex justify-content-between border-bottom border-secondary mb-2">
        <h2 class="main-header-style border-0">
          ${LangFranchiseeEngineeredToOrder.specialItem()}
          ${getSupportStatusBadge(this.branchQuoteSupport?.status ?? BranchQuoteSupportStatus.Draft)}
        </h2>
      </div>

      <form class="special-item-editor-container ">
        <div class="row">
          <div class="col-6 custom-item-props">
            ${dealerAccess
              ? forms.textRequired('title', LangFranchiseeEngineeredToOrder.title(), 120, {
                  change: () => {
                    if (this.branchQuoteSupport)
                      this.branchQuoteSupport.subject = forms.dataBinding.getValue('title') as string;
                  }
                })
              : forms.textReadonly('title', LangFranchiseeEngineeredToOrder.title())}
            ${dealerAccess
              ? forms.float('quantity', LangFranchiseeEngineeredToOrder.quantity(), {
                  events: { blur: this.eventChangedQuantity, keyup: this.eventChangedQuantity }
                })
              : forms.floatReadonly('quantity', LangFranchiseeEngineeredToOrder.quantity())}
            ${this.usingV6
              ? forms.text('description', LangFranchiseeEngineeredToOrder.description(), 120)
              : isSupplier
                ? forms.textRequired('description', LangFranchiseeEngineeredToOrder.supplierReference(), 120)
                : forms.textReadonly('description', LangFranchiseeEngineeredToOrder.supplierReference())}
            ${this.usingV6
              ? nothing
              : isSupplier
                ? forms.money('supplierGrossSingleUnitCost', LangFranchiseeEngineeredToOrder.supplierUnitPrice(), {
                    events: { blur: this.eventChangedSingleUnitCost, keyup: this.eventChangedSingleUnitCost }
                  })
                : forms.moneyReadonly(
                    'supplierGrossSingleUnitCost',
                    LangFranchiseeEngineeredToOrder.supplierUnitPrice()
                  )}
          </div>
          <div class="col-3 upload-zone">
            <bs-form-image-upload
              id="thumbnail"
              .resize="${true}"
              .showFileInput="${false}"
              .isThumbnail="${true}"
              .displayType="${1}"
              .resizeWidth="${600}"
              .resizeHeight="${500}"
              .value="${src}"
              .clickCaption=${isSupplier
                ? LangFranchiseeEngineeredToOrder.chooseFile()
                : LangFranchiseeEngineeredToOrder.supplierToUpload()}
              data-label="${LangFranchiseeEngineeredToOrder.thumbnail()}"
              .readonly="${!isSupplier || readonly}"
            ></bs-form-image-upload>
          </div>
          <div class="col-3 upload-zone last">${masterDocumentDropZoneTemplate}</div>
          <div class="col-12 custom-item-notes">
            ${this.createCommentRow(
              formsNonForced,
              LangFranchiseeEngineeredToOrder.notesToClient(),
              LangFranchiseeEngineeredToOrder.thisDetailIsPublishedOnQuoteReports(),
              customerNoteButton,
              handleCustomerNote
            )}
            ${this.createCommentRow(
              formsNonForced,
              LangFranchiseeEngineeredToOrder.notesToManufacturer(),
              LangFranchiseeEngineeredToOrder.thisDetailIsPublishedOnPurchaseOrderReport(),
              manufacturerNoteButton,
              handleManufacturerNote
            )}
          </div>
        </div>
      </form>
      <div class="conversation-note">${this.conversationNoteHeader()} ${this.conversationNoteTemplates()}</div>
    `;

    // Right column template
    const rightColumnTemplate = this.usingV6 ? tabGroup : convversationTemplate;

    return html`
      <div class="eto-main container form-two-col ">
        <div class="row">
          <div class="col-5 form-column">${leftColumnTemplate}</div>
          <div class="col-7 form-column">${rightColumnTemplate}</div>
        </div>
      </div>
    `;
  }

  private backupPriceDetails() {
    this._originalsupplierGrossSingleUnitCost = this._quoteItemContainer?.price.supplierGrossSingleUnitCost;
    this._originalQty = this._quoteItemContainer?.item.quantity;
    this._originalThumbnail = this._quoteItemContainer?.item.virtualThumbnailPath;
  }

  private async saveExistingItem(ssiInputs: InputUpdateQuoteItems | undefined): Promise<boolean> {
    if (!this.quoteItemContainer) return false;
    const thumbnail = this.thumbNailEditor?.isValueChanged ? await this.thumbNailEditor?.base64Value() : '';
    const thumbnailExtension = thumbnail !== '' ? this.thumbNailEditor?.fileExtension : undefined;

    const result = await this.qcm.saveAndUpdateQuoteItem(
      this.quoteItemContainer,
      thumbnail,
      ssiInputs,
      thumbnailExtension
    );
    if (!this.qcm.lastSaveSuccessful) return false;

    await this.thumbNailEditor?.ClearFiles();
    const frameDiff = this.quoteItemContainer.differenceReport as FrameCompareReport | null | undefined;
    this.quoteItemContainer = result;
    await this.updateMasterDocument();
    await this.createOrUpdateBranchQuoteSupport(frameDiff);

    this.dispatchSavedEvent();
    return true;
  }

  private async updateMasterDocument() {
    await this.masterDocumentEditor?.post();
  }

  private dispatchSavedEvent() {
    this.dispatchCustom('saved', {
      quoteItemContainer: this.quoteItemContainer,
      branchQuoteSupport: this.branchQuoteSupport,
      masterDocument: this.masterDocument,
      quoteItemConversationLink: this.quoteItemConversationLink
    });
  }

  private async createOrUpdateBranchQuoteSupport(frameDiff: FrameCompareReport | null | undefined) {
    if (!this.branchQuoteSupport) throw new DevelopmentError('ETO BranchQuoteSupport must be assigned');
    if (isEmptyOrSpace(this.branchQuoteSupport.recordVersion)) {
      return await this.assignBranchQuoteSupport();
    } else await this.updateBranchQuoteSupport(frameDiff);
    return true;
  }

  private async updateBranchQuoteSupport(frameDiff: FrameCompareReport | null | undefined) {
    const branchSupportSubject = `${this.quoteItemContainer?.item.title} - ${this.quoteItemContainer?.item.description}`;
    if (this.branchQuoteSupport && this.quoteItemContainer) {
      const markDown = getFrameCompareAsMarkdown('', frameDiff);
      const conversationEntries = isEmptyOrSpace(markDown)
        ? []
        : [
            {
              attachments: [],
              id: newGuid(),
              text: LangFranchiseeQuoteManager.quoteItemAttachToSupportRequestModifed(markDown, 'eto'),
              users: []
            }
          ];

      if (this.branchQuoteSupport?.subject !== branchSupportSubject || conversationEntries.length > 0) {
        this.branchQuoteSupport.subject = branchSupportSubject;
        this.branchQuoteSupport = (
          await getApiFactory()
            .franchisee()
            .updateBranchQuoteSupport({
              quoteItemLink: null,
              branchQuoteId: this.quoteId,
              masterDocumentConversationId: this.masterDocument?.id ?? null,
              branchQuoteSupportId: this.branchQuoteSupportId,
              subject: branchSupportSubject,
              status: null,
              quoteItemIdToDetach: null,
              conversationEntries: conversationEntries
            })
        )?.branchQuoteSupport;
        if (conversationEntries.length > 0) {
          fireQuickInformationToast(LangFranchiseeQuoteManager.supportRequestsUpdatedDueToItemChange(1, 'eto'), 5000);
        }
      }
    }
  }
}
